import { hoursToMilliseconds } from "date-fns/hoursToMilliseconds";
import { minutesToMilliseconds } from "date-fns/minutesToMilliseconds";

import { useAbsencesForToday } from "~api/absences.ts";
import { useConstructionSites } from "~api/constructionSites.ts";
import {
  useAllEmployees,
  useCurrentEmployeeRequest,
  useEmployees,
} from "~api/employees.ts";
import { useFeatures } from "~api/features.ts";
import { useManagers } from "~api/managers.ts";
import { useCurrentEmployeeTeamAssignments } from "~api/teamAssignments.ts";
import { useTeams } from "~api/teams.ts";
import { useAllTenants } from "~api/tenants.ts";
import { useAllTools, useBillableTools } from "~api/tools.ts";
import { useWorkPackages } from "~api/workPackages.ts";
import { Ability } from "~auth/Ability.ts";
import { usePermitted } from "~auth/usePermitted.ts";
import { useNotificationCount } from "~components/Notifications/api/notifications";
import { usePhotoUploadWorkPackages } from "~components/Resources/PhotoDocumentation/api/photoUploadWorkPackages.ts";
import { useToolsAdditionalData } from "~components/Resources/ToolAdministration/api/toolAdministration";
import {
  useCurrentEmployeeRunningTimeTracking,
  useCurrentEmployeeStoppedTimeTrackings,
} from "~components/TimeManagement/api/timeTracking.ts";
import { useTimeWorked } from "~components/TimeManagement/api/timeWorked";
import { useFirstPainted } from "~lib/firstPaint.ts";
import type { OfflinePrefetchOptions } from "./OfflinePrefetchOptions";
import { PrefetchQueue } from "./PrefetchQueue.ts";

export const MAX_CONCURRENT_PREFETCHES = 3;

const prefetchOptions: OfflinePrefetchOptions = {
  refetchInterval: minutesToMilliseconds(15),
  refetchIntervalInBackground: true,
  refetchOnWindowFocus: true,
  staleTime: minutesToMilliseconds(5),
  gcTime: hoursToMilliseconds(24) * 7,
};

// Large, rarely changing lists needed offline only as a fallback: fetched once per session, not per interval
const oneShotPrefetchOptions: Pick<
  OfflinePrefetchOptions,
  "staleTime" | "gcTime"
> = {
  staleTime: hoursToMilliseconds(12),
  gcTime: hoursToMilliseconds(24) * 7,
};

export function useOfflinePrefetch() {
  const {
    isSuccess: isFeaturesSuccess,
    isPlaceholderData: isFeaturesPlaceholder,
  } = useFeatures();
  const permitted = usePermitted();
  let queue = PrefetchQueue.start(useFirstPainted(), MAX_CONCURRENT_PREFETCHES);

  // Time tracking first: it is what a site needs most when the signal drops
  const teamAssignments = useCurrentEmployeeTeamAssignments(
    queue.options(prefetchOptions),
  );
  queue = queue.after(teamAssignments);
  const runningTimeTracking = useCurrentEmployeeRunningTimeTracking(
    queue.options(prefetchOptions),
  );
  queue = queue.after(runningTimeTracking);
  const stoppedTimeTrackings = useCurrentEmployeeStoppedTimeTrackings(
    queue.options(prefetchOptions),
  );
  queue = queue.after(stoppedTimeTrackings);
  // Time tracking reads work packages embedded in team assignments and time
  // trackings; this list only backs the offline task assignment dialog.
  const workPackages = useWorkPackages(
    { active: true },
    queue.options(oneShotPrefetchOptions),
  );
  queue = queue.after(workPackages);

  const employees = useEmployees({
    useQueryOptions: queue.options(prefetchOptions),
  });
  queue = queue.after(employees);
  const managers = useManagers({
    useQueryOptions: queue.options(prefetchOptions),
  });
  queue = queue.after(managers);
  const constructionSites = useConstructionSites({
    useQueryOptions: queue.options(prefetchOptions),
  });
  queue = queue.after(constructionSites);
  const currentEmployee = useCurrentEmployeeRequest(
    queue.options(prefetchOptions),
  );
  queue = queue.after(currentEmployee);
  const teams = useTeams({
    options: queue.options(prefetchOptions),
    includeDeleted: false,
  });
  queue = queue.after(teams);
  const absencesForToday = useAbsencesForToday(queue.options(prefetchOptions));
  queue = queue.after(absencesForToday);
  const billableTools = useBillableTools({}, queue.options(prefetchOptions));
  queue = queue.after(billableTools);
  const allTools = useAllTools({}, queue.options(prefetchOptions));
  queue = queue.after(allTools);
  const allEmployees = useAllEmployees(
    undefined,
    queue.options(prefetchOptions),
  );
  queue = queue.after(allEmployees);
  // useAllTenants always fetches every tenant and filters client-side, so
  // one prefetch here warms the cache for every includeInactive variant.
  const allTenants = useAllTenants(
    { includeInactive: true },
    queue.options(prefetchOptions),
  );
  queue = queue.after(allTenants);
  const timeWorked = useTimeWorked(queue.options(prefetchOptions));
  queue = queue.after(timeWorked);
  const notificationCount = useNotificationCount(
    queue.options(prefetchOptions),
  );
  queue = queue.after(notificationCount);
  const toolsAdditionalData = useToolsAdditionalData(
    queue.options(prefetchOptions),
  );
  queue = queue.after(toolsAdditionalData);

  const shouldPrefetchPhotoUploadWorkPackages = permitted([
    Ability.canCreatePhotoDocumentation,
  ]);
  const photoUploadWorkPackages = usePhotoUploadWorkPackages({
    ...prefetchOptions,
    enabled: queue.enabled && shouldPrefetchPhotoUploadWorkPackages,
  });
  const isPhotoUploadWorkPackagesSuccess = shouldPrefetchPhotoUploadWorkPackages
    ? photoUploadWorkPackages.isSuccess
    : true;

  return (
    isFeaturesSuccess &&
    !isFeaturesPlaceholder &&
    teamAssignments.isSuccess &&
    runningTimeTracking.isSuccess &&
    stoppedTimeTrackings.isSuccess &&
    workPackages.isSuccess &&
    employees.isSuccess &&
    managers.isSuccess &&
    constructionSites.isSuccess &&
    currentEmployee.isSuccess &&
    teams.isSuccess &&
    absencesForToday.isSuccess &&
    billableTools.isSuccess &&
    allTools.isSuccess &&
    allEmployees.isSuccess &&
    allTenants.isSuccess &&
    timeWorked.isSuccess &&
    notificationCount.isSuccess &&
    toolsAdditionalData.isSuccess &&
    isPhotoUploadWorkPackagesSuccess
  );
}
